import { useRef, useState } from 'react';
import Reveal from '@/components/base/Reveal';
import { cases, caseNiches } from '@/mocks/cases';
import { communityUrl } from '@/mocks/strategix';

const tabs = ['Все', ...caseNiches];
const PER_PAGE = 6;

function getPageItems(current: number, total: number): (number | 'gap')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const items: (number | 'gap')[] = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) items.push('gap');
  for (let i = start; i <= end; i += 1) items.push(i);
  if (end < total - 1) items.push('gap');
  items.push(total);
  return items;
}

export default function CasesGrid() {
  const [active, setActive] = useState('Все');
  const [page, setPage] = useState(1);
  const gridRef = useRef<HTMLDivElement>(null);

  const filtered = active === 'Все' ? cases : cases.filter((item) => item.niche === active);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const safePage = Math.min(page, totalPages);
  const startIndex = (safePage - 1) * PER_PAGE;
  const paginated = filtered.slice(startIndex, startIndex + PER_PAGE);

  const selectTab = (tab: string) => {
    setActive(tab);
    setPage(1);
  };

  const goToPage = (next: number) => {
    setPage(next);
    gridRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section className="relative w-full px-5 md:px-10 pb-20 md:pb-28">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-wrap items-center justify-center gap-2">
          {tabs.map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => selectTab(tab)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                active === tab
                  ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                  : 'bg-background-100 text-foreground-700 border border-background-200/70 hover:border-primary-400/60'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        <div ref={gridRef} className="scroll-mt-24 mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {paginated.map((item, index) => (
            <Reveal key={item.id} delay={index * 70} className="h-full">
              <article className="group h-full flex flex-col p-6 rounded-lg bg-background-100/70 border border-background-200/70 hover:border-primary-400/60 transition-colors">
                <div className="flex items-center justify-between gap-3">
                  <span className="px-3 py-1 rounded-full bg-secondary-200/80 text-secondary-950 text-xs whitespace-nowrap">
                    {item.niche}
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-xs text-foreground-600 whitespace-nowrap">
                    <i className={item.platformIcon} style={{ color: item.platformColor }} aria-hidden="true"></i>
                    {item.platform}
                  </span>
                </div>

                <h3 className="mt-5 font-heading text-base md:text-lg text-foreground-950">{item.company}</h3>
                <p className="mt-1 text-xs text-foreground-500">Срок: {item.duration}</p>
                <p className="mt-3 text-sm text-foreground-600 leading-relaxed">{item.task}</p>

                <div className="mt-5 grid grid-cols-2 gap-2.5">
                  {item.metrics.map((metric) => (
                    <div
                      key={metric.label}
                      className="rounded-md bg-background-50 border border-background-200/70 p-3 text-center"
                    >
                      <div className="font-heading font-bold text-lg text-primary-500 whitespace-nowrap">
                        {metric.value}
                      </div>
                      <div className="mt-1 text-[11px] leading-snug text-foreground-600">{metric.label}</div>
                    </div>
                  ))}
                </div>

                <p className="mt-4 text-sm text-foreground-700 leading-relaxed border-t border-background-200/70 pt-4 flex-1">
                  {item.solution}
                </p>
              </article>
            </Reveal>
          ))}
        </div>

        {totalPages > 1 && (
          <div className="mt-10 flex flex-col items-center gap-4">
            <p className="text-sm text-foreground-600">
              Показано {startIndex + 1}–{Math.min(startIndex + PER_PAGE, filtered.length)} из {filtered.length} кейсов
            </p>
            <div className="flex flex-wrap items-center justify-center gap-2">
              <button
                type="button"
                onClick={() => goToPage(safePage - 1)}
                disabled={safePage === 1}
                className="w-10 h-10 inline-flex items-center justify-center rounded-md bg-background-100 text-foreground-700 border border-background-200/70 hover:border-primary-400/60 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                aria-label="Предыдущая страница"
              >
                <i className="ri-arrow-left-s-line text-lg"></i>
              </button>

              {getPageItems(safePage, totalPages).map((item, index) =>
                item === 'gap' ? (
                  <span key={`gap-${index}`} className="px-1 text-foreground-500 select-none">
                    …
                  </span>
                ) : (
                  <button
                    key={item}
                    type="button"
                    onClick={() => goToPage(item)}
                    className={`w-10 h-10 inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors cursor-pointer ${
                      item === safePage
                        ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                        : 'bg-background-100 text-foreground-700 border border-background-200/70 hover:border-primary-400/60'
                    }`}
                    aria-current={item === safePage ? 'page' : undefined}
                  >
                    {item}
                  </button>
                )
              )}

              <button
                type="button"
                onClick={() => goToPage(safePage + 1)}
                disabled={safePage === totalPages}
                className="w-10 h-10 inline-flex items-center justify-center rounded-md bg-background-100 text-foreground-700 border border-background-200/70 hover:border-primary-400/60 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                aria-label="Следующая страница"
              >
                <i className="ri-arrow-right-s-line text-lg"></i>
              </button>
            </div>
          </div>
        )}

        <Reveal delay={120}>
          <div className="mt-12 flex flex-col lg:flex-row items-start lg:items-center gap-5 p-6 md:p-7 rounded-lg border border-accent-500/40 bg-accent-500/10">
            <span className="w-12 h-12 flex-none flex items-center justify-center rounded-md bg-accent-500/20 text-accent-300">
              <i className="ri-line-chart-line text-2xl"></i>
            </span>
            <div className="flex-1">
              <h3 className="font-heading text-base md:text-lg text-foreground-950">Хотите такой же результат?</h3>
              <p className="mt-2 text-sm text-foreground-700 leading-relaxed">
                Расскажите о своём сообществе — подберём стратегию под вашу нишу и бюджет. Наполнение тарифов
                уточняйте в сообществе.
              </p>
            </div>
            <a
              href={communityUrl}
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="w-full lg:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-accent-500 text-background-50 text-sm font-semibold hover:bg-accent-400 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-vk-line text-base"></i>
              Перейти в сообщество
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}